import { useState, useEffect } from 'react'

import {

  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { FormInput } from '../ui'

import BusinessIcon from '@mui/icons-material/Business'

import { toast } from 'react-toastify'

import { contactsApi, CreateContactPayload } from '../../lib/api/contactsApi'

import { customersApi } from '../../lib/api/customersApi'

import { orgsApi, lobsApi, Org, Lob } from '../../lib/api/tenantsApi'

import { themeColors } from '../../app/layout/theme'

import {

  EntityFormDrawer,

  EntityFormSection,

  EntityFormField,

  entityDrawerInputSx,
  EntityDrawerSelect,
  EntityDrawerInput,

} from '../shared/EntityFormDrawer'

import { CustomPhoneInput } from '../ui/CustomPhoneInput'

import contactLogo from '../../assests/drawerIcons/contactLogo.svg'



interface CustomerOption {

  id: string

  label: string

  orgId: string | null

}



const ROLE_OPTIONS = ['Manager', 'CEO', 'CFO', 'Director', 'Executive', 'Owner', 'Supervisor', 'Staff']



export interface ContactDrawerProps {

  open: boolean

  onClose: () => void

  onCreated: () => void

  isSuperAdmin?: boolean

  initialCustomer?: CustomerOption | null

}



export function ContactDrawer({ open, onClose, onCreated, isSuperAdmin, initialCustomer }: ContactDrawerProps) {

  const [form, setForm] = useState({ name: '', phone: '', email: '', role: '' })

  const [customer, setCustomer] = useState<CustomerOption | null>(null)

  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([])

  const [errors, setErrors] = useState<Record<string, string>>({})

  const [saving, setSaving] = useState(false)

  const [loadingCustomers, setLoadingCustomers] = useState(false)



  const [orgs, setOrgs] = useState<Org[]>([])

  const [lobs, setLobs] = useState<Lob[]>([])

  const [orgId, setOrgId] = useState('')

  const [lobId, setLobId] = useState('')



  // When initialCustomer is provided we're in "locked" mode — the company

  // is pre-determined (opened from the Company detail page). Skip loading

  // the full customer list and the org tenant selectors.

  const isLocked = !!initialCustomer



  useEffect(() => {

    if (!open) return



    if (initialCustomer) {

      setCustomer(initialCustomer)

      // Org is already determined by the company — no need to load orgs

      return

    }



    setLoadingCustomers(true)

    customersApi

      .getAll({ pageSize: 100 })

      .then(res =>

        setCustomerOptions(

          res.items.map(c => ({

            id: c.id,

            label: c.companyName ? `${c.name} (${c.companyName})` : c.name,

            orgId: c.orgId,

          })),

        ),

      )

      .catch(() => toast.error('Failed to load customers'))

      .finally(() => setLoadingCustomers(false))



    if (isSuperAdmin) orgsApi.getAll().then(setOrgs).catch(() => { })

  }, [open, isSuperAdmin, initialCustomer])



  useEffect(() => {

    setLobId('')

    setLobs([])

    if (orgId) lobsApi.getAll(orgId).then(setLobs).catch(() => { })

  }, [orgId])



  const set = (k: string, v: string) => {

    setForm(f => ({ ...f, [k]: v }))

    setErrors(e => ({ ...e, [k]: '' }))

  }



  const validate = (): boolean => {

    const e: Record<string, string> = {}

    if (!form.name.trim()) e.name = 'Full name is required'

    if (!form.phone.trim()) e.phone = 'Phone is required'

    if (!customer) e.customer = 'Please select a customer'

    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email'

    setErrors(e)

    return Object.keys(e).length === 0

  }



  const handleClose = () => {

    setForm({ name: '', phone: '', email: '', role: '' })

    // Only clear customer if it wasn't locked from the parent context

    if (!isLocked) setCustomer(null)

    setErrors({})

    setOrgId('')

    setLobId('')

    onClose()

  }



  const submit = async () => {

    if (!validate()) return

    setSaving(true)

    try {

      const payload: CreateContactPayload = {

        name: form.name.trim(),

        phone: form.phone.trim(),

        customerId: customer!.id,

        ...(form.email && { email: form.email.trim() }),

        ...(form.role && { role: form.role.trim() }),

        ...(isSuperAdmin && orgId && { orgId }),

        ...(isSuperAdmin && lobId && { lobId }),

      }

      await contactsApi.create(payload)

      toast.success('Contact created')

      handleClose()

      onCreated()

    } catch (err: any) {

      toast.error(err?.response?.data?.message ?? 'Failed to create contact')

    } finally {

      setSaving(false)

    }

  }



  const fieldSx = {

    ...entityDrawerInputSx,

    '& .MuiInputBase-input': { fontSize: 13, py: 1.125 },

  }



  return (

    <EntityFormDrawer

      open={open}

      title="New Contact"

      icon={<img src={contactLogo} alt="contact" style={{ width: 20, height: 20 }} />}

      onClose={handleClose}

      onSubmit={submit}

      submitLabel="Create Contact"

      isSubmitting={saving}

    >      <EntityFormSection columns={1}>
        <EntityDrawerInput
          label="Full Name"
          required
          value={form.name}
          onChange={v => set('name', v)}
          placeholder="e.g. John Smith"
          error={errors.name}
        />

        {isLocked ? (
          <EntityFormField label="Company">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Chip
                icon={<BusinessIcon sx={{ fontSize: '15px !important' }} />}
                label={initialCustomer!.label}
                size="small"
                sx={{
                  bgcolor: themeColors.primary + '14',
                  color: themeColors.primary,
                  fontWeight: 600,
                  fontSize: 12,
                  border: `1px solid ${themeColors.primary}30`,
                  '& .MuiChip-icon': { color: themeColors.primary },
                }}
              />
            </Box>
          </EntityFormField>
        ) : (
          <EntityFormField label="Customer" required>
            <Autocomplete
              size="small"
              options={customerOptions}
              value={customer}
              onChange={(_e, v) => {
                setCustomer(v)
                setErrors(er => ({ ...er, customer: '' }))
                if (v?.orgId) {
                  setOrgId(v.orgId)
                }
              }}
              loading={loadingCustomers}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={params => (
                <FormInput
                  {...params}
                  placeholder="Search customers..."
                  error={!!errors.customer}
                  helperText={errors.customer}
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCustomers ? <CircularProgress size={14} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </EntityFormField>
        )}

        <EntityFormField label="Phone" required>
          <CustomPhoneInput
            value={form.phone}
            onChange={e => set('phone', e.target.value)}
            placeholder="e.g. 9876543210"
            error={!!errors.phone}
            helperText={errors.phone}
            sx={fieldSx}
          />
        </EntityFormField>

        <EntityDrawerInput
          label="Email"
          fieldType="email"
          value={form.email}
          onChange={v => set('email', v)}
          placeholder="example@email.com"
          error={errors.email}
        />

        <EntityFormField label="Role / Title">
          <Autocomplete
            size="small"
            freeSolo
            options={ROLE_OPTIONS}
            value={form.role || null}
            onInputChange={(_e, v) => set('role', v)}
            renderInput={params => (
              <FormInput
                {...params}
                placeholder="e.g. Manager, CEO"
                sx={fieldSx}
              />
            )}
          />
        </EntityFormField>
      </EntityFormSection>

      {isSuperAdmin && !isLocked && (
        <Box sx={{ mt: 0.5 }}>
          <Divider sx={{ my: 1.5 }}>
            <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, px: 1 }}>
              Tenant Assignment
            </Typography>
          </Divider>

          <EntityFormSection columns={1}>
            <EntityDrawerSelect
              label="Organisation"
              value={orgId}
              onChange={v => setOrgId(v)}
              options={orgs.map(o => ({ label: o.name, value: o.id }))}
              placeholder="Auto (first org)"
              disabled={!!customer}
            />

            {orgId && (
              <EntityDrawerSelect
                label="Line of Business"
                value={lobId}
                onChange={v => setLobId(v)}
                options={lobs.map(l => ({ label: l.name, value: l.id }))}
                placeholder="Any LOB"
              />
            )}
          </EntityFormSection>
        </Box>
      )}
    </EntityFormDrawer>
  )
}